import { Component, EventEmitter, Output, Input, OnInit, OnChanges} from '@angular/core';
import { FormControl, FormGroup, Validators, AbstractControl, ValidatorFn} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { SnackbarService } from '@core/services/snackbar.service';
import { DriverService } from '@modules/policy/services/driver/driver.service';
import { PolicyDataService } from '@modules/policy/services/shared/policy-data.service';
import { lengthValidator } from 'collabor8-funcs';

@Component({
  selector: 'app-driver-search',
  templateUrl: './driver-search.component.html',
  styleUrls: ['./driver-search.component.scss']
})
export class DriverSearchComponent implements OnInit, OnChanges {
  @Input() tabType: string;
  @Input() nodeId: number;
  @Input() clientForm: FormGroup;
  @Output() searchFormCreated = new EventEmitter<FormGroup>();
  @Output() driverFound = new EventEmitter<Object>();
  @Output() customerNumChange = new EventEmitter<{ value: any, isEmpty: boolean }>();
  @Output() addAsDriverChange = new EventEmitter<boolean>();

  readonly SEARCH_FIELDS = {
    CUSTOMER_NUM: 'DriverCustomerID',
    VALIDATION_NUM: 'DriverValidationNumberNum',
    CONSENT_IND: "DriverLookupConsentInd",
    ADD_AS_DRIVER: "AddContactAsDriverInd"
  }
  static lengthValidator(min: number, max: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const result = lengthValidator(min, max)(control.value);
      if (result && result.message){
        return { message: result.message };
      } 
      return result;
    };
  }
  public enableSearch = new FormGroup({
    [this.SEARCH_FIELDS.ADD_AS_DRIVER]: new FormControl(false)
  })
  private searchForm = new FormGroup({
    [this.SEARCH_FIELDS.CUSTOMER_NUM]: new FormControl(null, [Validators.required, DriverSearchComponent.lengthValidator(8, 8), Validators.pattern(/^[0-9]*$/)]),
    [this.SEARCH_FIELDS.VALIDATION_NUM]: new FormControl(null, [Validators.pattern(/^[0-9]*$/)]),
    [this.SEARCH_FIELDS.CONSENT_IND]: new FormControl(null),
    
  });

  private policyId: number;
  public customerNumControl: FormControl;
  public validationNumControl: FormControl;

  constructor(
    public policyDataService: PolicyDataService,
    private snackbar: SnackbarService,
    private driverService: DriverService,
    private route: ActivatedRoute,
  ) { // Subscribe to checkbox changes to toggle search fields (only on non-driver tabs)
    this.enableSearch.get(this.SEARCH_FIELDS.ADD_AS_DRIVER).valueChanges.subscribe((checked) => {
      if (this.tabType !== 'driver') {
        // Don't enable if parent has disabled the checkbox (client search was done)
        if (this.enableSearch.disabled) return;
        this.toggleDriverSearchFields(checked);
        this.addAsDriverChange.emit(checked);
      }
      
    });}

  onCustomerNumChange(value: any) {
    const isEmpty = value === '' || value === null || value === undefined;
    this.customerNumChange.emit({ value, isEmpty });
  }

  ngOnInit() {
    this.customerNumControl = this.searchForm.get(this.SEARCH_FIELDS.CUSTOMER_NUM) as FormControl;
    this.validationNumControl = this.searchForm.get(this.SEARCH_FIELDS.VALIDATION_NUM) as FormControl;
    this.onCustomerNumChange(this.customerNumControl.value);
    //this.nodeId = Number(this.route.snapshot.params["formId"]);
    this.policyId = Number(this.policyDataService.getPolicyID());
    this.searchFormCreated.emit(this.searchForm);  // emit early - before async initForm() runs

    
  }

  ngOnChanges(changes) {

    
    if ((changes.clientForm || changes.nodeId) && this.clientForm && this.nodeId) {
      this.policyId = Number(this.policyDataService.getPolicyID());

      // Reset forms to enabled state when nodeId changes (new form)
      // Cache loading will re-disable if needed
      if (changes.nodeId) {
        this.enableSearch?.enable();
        this.searchForm?.enable();
        this.enableSearch?.get(this.SEARCH_FIELDS.ADD_AS_DRIVER).setValue(false);
      }
      
      // Add searchForm to parent form so it saves together
      if (!this.clientForm.contains("DriverSearch")) {
        this.clientForm.addControl("DriverSearch", this.searchForm);
      }
      // Add enableSearch to parent form so checkbox persists
      if (!this.clientForm.contains("EnableDriverSearch")) {
        this.clientForm.addControl("EnableDriverSearch", this.enableSearch);
      }

      // Emit searchForm first so parent can disable if needed (e.g. clientKey is set)
      this.searchFormCreated.emit(this.searchForm);

      // On driver tab, enable by default; other tabs depend on checkbox
      // Skip if parent has already disabled the form
      if (!this.enableSearch.disabled) {
        if (this.tabType === 'driver') {
          this.searchForm.enable();
        } else {
          const addAsDriverChecked = this.enableSearch.get(this.SEARCH_FIELDS.ADD_AS_DRIVER).value;
          this.toggleDriverSearchFields(addAsDriverChecked);
        }
      }
      
      // Load cached values into search form (may disable if DriverLookupPerformedInd)
      this.loadFromCache();
      
      this.searchForm.markAllAsTouched();
      if (this.customerNumControl) {
        this.customerNumControl.updateValueAndValidity();
        this.onCustomerNumChange(this.customerNumControl.value);
      }
    }
  }

  toggleDriverSearchFields(addAsDriverChecked: boolean) {
    if (addAsDriverChecked) {
      this.searchForm.enable();
    } else {
      this.searchForm.disable();
    }
  }

  private loadFromCache() {
    // Only run if nodeId exists and searchForm has controls
    if (!this.nodeId || Object.keys(this.searchForm.controls).length === 0) return;
    
    const cachedForm = this.policyDataService.getValueFromCache(this.nodeId);
    if (!cachedForm) return;

    for (const [fieldName, control] of Object.entries(this.searchForm.controls)) {
      const value = cachedForm[fieldName] ?? null;
      control.setValue(value);
      control.updateValueAndValidity();
    }

    // Load enableSearch checkbox value from cache
    const addAsDriverValue = cachedForm[this.SEARCH_FIELDS.ADD_AS_DRIVER] ?? false;
    this.enableSearch.get(this.SEARCH_FIELDS.ADD_AS_DRIVER).setValue(addAsDriverValue);
    
    // Disable all controls if lookup already performed
    if (cachedForm["DriverLookupPerformedInd"]) {
      this.searchForm.disable();
      this.enableSearch.disable();
    }
    
    // Sync customerNumIsEmpty after populating from cache (setValue doesn't trigger input event)
    const cachedCustomerNum = cachedForm[this.SEARCH_FIELDS.CUSTOMER_NUM];
    this.onCustomerNumChange(cachedCustomerNum);
  }

  disableDriverSearch() {
    return !Object.values(this.searchForm?.controls).every(control => control.value) || this.searchForm.disabled || !this.searchForm.valid
  }

  driverSearch() {
    if (this.disableDriverSearch()) return;
    this.searchForm.controls[this.SEARCH_FIELDS.CONSENT_IND].disable(); // prevent user from revoking consent after searching
    this.snackbar.open(`Searching for driver...`);
    //const { AddContactAsDriverInd, ...searchFormData } = this.searchForm.getRawValue();
    this.driverService.getAbstractSGI(this.searchForm.getRawValue(), this.policyId, this.nodeId, this.tabType)
      .toPromise().then((res) => {
        this.policyDataService.fetchAndUpdatePolicyStates();
        if (!res.data) {
          return this.snackbar.open("Driver not found");
        }
        this.snackbar.open("Driver found");
        this.searchForm.disable(); //One successful driver lookup per driver
        this.enableSearch.disable();
        this.driverFound.emit(res.data);
      })
  }

}